import { MultiPolygon, Polygon } from "geojson";
import { SelectRecipientsTableQuery, TenureType } from "@gql/sites-api/graphql";

import { AvailableCountryCodes } from "../../typings/CountryCode";
import Feature from "src/js/stores/user/Feature";
import { Recipient } from "../../typings/Recipient";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { parseAddress } from "../../helpers/addressParser";
import { singleRecipientValidationSchema } from "../../forms/SelectRecipientsForm/SingleRecipientValidationSchema";
import { v4 as uuid } from "uuid";

// @todo this is a smell, we should be using the useTranslate hook. The hook was
// being used outside the context of a React component so I updated it to use
// the translate function directly in lieu of a full refactor.
import { translate as t } from "react-migration/lib/i18n/useTranslation";

function convertToRecipient(
  title: string,
  propertyAddress: string,
  siteId: string,
  geometry: Polygon | MultiPolygon,
  isGranted: boolean,
  siteBoundaryImageUrl: string,
  name?: string,
  address?: string,
  siteTitle?: string
): Recipient {
  const { addressLine1, city, region, postcode, countryCode } = parseAddress(address);

  const parsedAddress = {
    _id: uuid(),
    title,
    propertyAddress,
    fullName: name ?? t("sites.letters.unknown_owner"),
    addressLine1: addressLine1 || t("sites.letters.unknown_address"),
    city,
    region,
    postCode: postcode,
    fullAddress: address || t("sites.letters.unknown_address"),
    companyOwner: "",
    countryCode,
    siteTitle,
    siteId,
    isGranted,
    geometry,
  };

  const isValid = singleRecipientValidationSchema.safeParse(parsedAddress);

  return {
    ...parsedAddress,
    isValid: isValid.success,
    siteBoundaryImageUrl: siteBoundaryImageUrl,
  };
}

export function sitesToRecipients({
  _id,
  address,
  title: siteTitle,
  titles,
  geometry: _geometry,
  boundary_image: { uri: siteBoundaryImageUrl },
  lettersAccess: { isGranted },
}: SelectRecipientsTableQuery["selectRecipientsTable"]["edges"][0]["node"]): Recipient[] {
  const isUsUser = hasFeature(Feature.usAccess);

  const geometry = _geometry ?? { type: "Polygon", coordinates: [] };

  return (
    titles?.reduce((result, title) => {
      const { owners, title_no, tenure } = title;
      if (!owners || owners.length === 0) {
        let unknownName = "";
        switch (tenure) {
          case TenureType.Freehold:
            unknownName = t("sites.letters.unknown_freeholder");
            break;
          case TenureType.Leasehold:
            unknownName = t("sites.letters.unknown_leaseholder");
            break;
          case TenureType.UnknownOther:
          default:
            unknownName = t("sites.letters.unknown_owner");
        }
        result.push({
          _id: uuid(),
          title: title_no ?? "",
          propertyAddress: address ?? "",
          fullName: unknownName,
          addressLine1: t("sites.letters.unknown_address"),
          city: "",
          region: "",
          postCode: "",
          fullAddress: t("sites.letters.unknown_address"),
          siteTitle: siteTitle ?? "",
          isGranted,
          isValid: false,
          siteId: _id,
          siteBoundaryImageUrl: siteBoundaryImageUrl ?? "",
          geometry: geometry ?? { type: "Polygon", coordinates: [] },
          countryCode: isUsUser ? AvailableCountryCodes.UNITED_STATES : AvailableCountryCodes.UNITED_KINGDOM,
        });
      } else {
        owners.forEach(({ company, person }) => {
          if (person) {
            result.push(
              convertToRecipient(
                title_no ?? "",
                address ?? "",
                _id,
                geometry,
                isGranted,
                siteBoundaryImageUrl,
                person.name ?? "",
                person.address ?? "",
                siteTitle ?? ""
              )
            );
          }
          if (company) {
            result.push(
              convertToRecipient(
                title_no ?? "",
                address ?? "",
                _id,
                geometry,
                isGranted,
                siteBoundaryImageUrl,
                company.name ?? "",
                company.addresses?.[0] ?? "",
                siteTitle ?? ""
              )
            );
          }
        });
      }
      return result;
    }, [] as Recipient[]) ?? []
  );
}
